import React, { useEffect, useState } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";

export default function FeaturedPosterCarousel() {
  const [posters, setPosters] = useState([]);
  const [visible, setVisible] = useState(3);
  const [index, setIndex] = useState(0);
  const [anim, setAnim] = useState(true);
  const [paused, setPaused] = useState(false);
  const touchStartX = React.useRef(null);

  useEffect(() => {
    api.get("/featured-posters").then((response) => {
      setPosters(response.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setVisible(mq.matches ? 1 : 3);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const canLoop = posters.length > 1;

  useEffect(() => {
    setIndex(0);
    setAnim(true);
  }, [visible, posters.length]);

  useEffect(() => {
    if (!canLoop || paused) return undefined;
    const id = window.setInterval(() => setIndex((i) => i + 1), 8000);
    return () => window.clearInterval(id);
  }, [canLoop, paused]);

  useEffect(() => {
    if (anim) return undefined;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
    return () => cancelAnimationFrame(raf);
  }, [anim]);

  if (posters.length === 0) {
    return null;
  }

  const loopSlides = canLoop ? [...posters, ...posters.slice(0, visible)] : posters;

  const handleTransitionEnd = () => {
    if (canLoop && index >= posters.length) {
      setAnim(false);
      setIndex((i) => i - posters.length);
    }
  };

  const previous = () => {
    setAnim(true);
    setIndex((i) => (i - 1 + posters.length) % posters.length);
  };

  const next = () => {
    setAnim(true);
    setIndex((i) => i + 1);
  };

  const activeDot = ((index % posters.length) + posters.length) % posters.length;

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || !canLoop) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) { delta < 0 ? next() : previous(); }
    touchStartX.current = null;
  };

  return (
    <section
      className="bg-warm-surface py-20 border-b border-amber-900/10"
      data-testid="featured-posters-section"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="max-w-2xl mb-10">
          <div className="text-amber-700 text-sm font-semibold tracking-widest uppercase mb-3">
            Featured
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-maroon leading-tight">
            Travel opportunities worth a closer look
          </h2>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          data-testid="featured-posters-carousel"
        >
          <div className="poster-viewport">
            <div
              className="poster-track"
              style={{
                transform: `translateX(-${index * (100 / visible)}%)`,
                transition: anim ? "transform 1000ms ease-in-out" : "none",
                "--poster-basis": `${100 / visible}%`,
              }}
              onTransitionEnd={handleTransitionEnd}
            >
              {loopSlides.map((poster, slideIndex) => {
                const tourHref = poster.tour?.slug ? `/tours/${poster.tour.slug}` : `/tours/${poster.tourId}`;
                return (
                <article
                  className="poster-slide"
                  key={`${poster.id}-${slideIndex}`}
                >
                  <div className="group">
                    <div className="relative overflow-hidden rounded-2xl shadow-xl bg-white aspect-[4/5] ring-1 ring-amber-900/10">
                      {poster.tourId ? (
                        <Link to={tourHref} className="block w-full h-full">
                          <img
                            src={fileUrl(poster.imageUrl)}
                            alt={poster.caption || "Global Compass featured travel poster"}
                            className="w-full h-full object-cover"
                            data-testid={`featured-poster-${poster.id}`}
                          />
                        </Link>
                      ) : (
                        <img
                          src={fileUrl(poster.imageUrl)}
                          alt={poster.caption || "Global Compass featured travel poster"}
                          className="w-full h-full object-cover"
                          data-testid={`featured-poster-${poster.id}`}
                        />
                      )}
                    </div>
                    <div className="mt-4 text-center px-2 min-h-[64px]">
                      {poster.caption && (
                        <p className="text-slate-600 text-sm leading-relaxed">{poster.caption}</p>
                      )}
                      {poster.tourId && poster.tour && (
                        <Link
                          to={tourHref}
                          className="mt-2 text-maroon font-semibold inline-flex items-center gap-1.5 hover:text-amber-700 transition-colors text-sm"
                          data-testid={`featured-poster-tour-link-${poster.id}`}
                        >
                          Explore {poster.tour.title}
                          <ArrowUpRight size={15} />
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
                );
              })}
            </div>
          </div>

          {canLoop && (
            <>
              <button
                type="button"
                onClick={previous}
                aria-label="Previous featured posters"
                className="absolute -left-2 lg:-left-5 top-[36%] -translate-y-1/2 w-11 h-11 rounded-full bg-maroon/90
                  hover:bg-maroon text-amber-50 grid place-items-center shadow-lg transition-colors"
                data-testid="featured-poster-previous"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next featured posters"
                className="absolute -right-2 lg:-right-5 top-[36%] -translate-y-1/2 w-11 h-11 rounded-full bg-maroon/90
                  hover:bg-maroon text-amber-50 grid place-items-center shadow-lg transition-colors"
                data-testid="featured-poster-next"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
        </div>

        {canLoop && (
          <div className="flex justify-center gap-2 mt-8" data-testid="featured-poster-dots">
            {posters.map((poster, dotIndex) => (
              <button
                type="button"
                key={poster.id}
                onClick={() => { setAnim(true); setIndex(dotIndex); }}
                aria-label={`Show featured poster ${dotIndex + 1}`}
                className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${
                  dotIndex === activeDot ? "w-8 bg-maroon" : "w-2.5 bg-amber-300"
                }`}
                data-testid={`featured-poster-dot-${dotIndex}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
